import React, { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Heart } from "lucide-react";
import api from "../lib/api";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export default function DonorWall() {
    const [wall, setWall] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/donors/wall")
            .then((r) => setWall(r.data.donations || []))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
            <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-primary mb-3">The wall</p>
                <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight leading-tight">
                    Every dollar has a name behind it.
                </h1>
                <p className="text-muted-foreground text-lg mt-4 leading-relaxed">
                    A public thank-you to everyone funding Hands of Hope through Real-Check.
                    Messages are optional. All amounts are real, verified via Stripe.
                </p>
            </div>

            {loading ? (
                <p className="text-muted-foreground text-sm">Loading the wall…</p>
            ) : wall.length === 0 ? (
                <Card className="card-surface p-10 text-center rounded-2xl" data-testid="donor-wall-empty">
                    <Heart className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-display text-2xl text-accent mb-2">Be the first name on the wall.</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm">
                        Kick off the mission with the founding donation. Every dollar goes to Hands of Hope.
                    </p>
                    <Link to="/donate">
                        <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                            <Heart className="h-4 w-4 mr-2" /> Make the first donation
                        </Button>
                    </Link>
                </Card>
            ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="donor-wall">
                    {wall.map((d, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: i * 0.02 }}
                            className="card-surface rounded-2xl p-6"
                            data-testid={`donor-wall-item-${i}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-full bg-primary/25 border border-primary/40 flex items-center justify-center font-display font-bold text-accent shrink-0">
                                        {(d.donor_name?.[0] || "?").toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-accent font-medium truncate">{d.donor_name}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {d.at ? new Date(d.at).toLocaleDateString() : ""}
                                        </div>
                                    </div>
                                </div>
                                <div className="font-display font-bold text-lg text-primary shrink-0">
                                    ${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                            {d.donor_message && (
                                <p className="mt-4 text-sm text-muted-foreground italic leading-relaxed">
                                    &ldquo;{d.donor_message}&rdquo;
                                </p>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}

            <div className="mt-12 text-center">
                <Link to="/donate">
                    <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6" data-testid="donor-wall-donate-cta">
                        <Heart className="h-4 w-4 mr-2" /> Join the wall
                    </Button>
                </Link>
            </div>
        </div>
    );
}
